<!--
  ============================================================================
  PdfBlock.vue - Involucro di PdfViewer con caricamento pigro e ricaduta
  ============================================================================

  PERCHE' ESISTE:
  `PdfViewer` dipende da `vue-pdf-embed`, che sono 2,6 MB: il 99,9% dell'intero
  chunk-vendors. Importato staticamente finiva nel bundle principale, quindi
  veniva scaricato e interpretato su OGNI pagina del sito, anche se il
  visualizzatore serve a una sola sezione di una sola pagina.

  Due conseguenze, entrambe risolte qui:

  1. PESO - ora la libreria sta in un chunk a parte, chiesto solo quando c'e'
     davvero un PDF da mostrare. Tutte le altre pagine non la scaricano.

  2. COMPATIBILITA' - pdf.js usa `??` (iOS 13.4) nel chunk, e nel suo WORKER
     campi privati e un blocco statico (iOS 16.4). Con l'import statico il `??`
     faceva fallire il PARSING di chunk-vendors, e sotto iOS 13.4 moriva il
     sito INTERO, non solo il PDF. Ora quel codice sta in un chunk separato: se
     il dispositivo non ce la fa, salta solo questo riquadro e la pagina vive.

  LA RICADUTA:
  Sotto iOS 16.4 si mostra un link diretto al file. Non e' una resa: Safari
  apre i PDF nativamente e bene, spesso meglio del visualizzatore in pagina, e
  senza scaricare 2,4 MB di libreria.

  TRE STRADE PORTANO ALLA RICADUTA:
  • il controllo di sintassi qui sotto, che chiede al motore se sa analizzare
    la grammatica del worker di pdf.js
  • il fallimento del caricamento del chunk (rete giu', sintassi non
    interpretabile)
  • ?legacy=1, per poterla ispezionare su un browser moderno

  ATTENZIONE NEGLI STILI:
  qui NON si usa `gap` dentro `display:flex`, perche' e' supportato solo da
  iOS 14.5. Questo riquadro deve vedersi bene proprio sui dispositivi vecchi:
  usare `gap` significherebbe romperlo esattamente dove serve. Margini.
  ============================================================================
-->

<template>
  <div class="pdf-block">
    <component
      :is="PdfViewerAsync"
      v-if="canShowViewer"
      :src="src"
      :title="title"
    />

    <div v-else class="pdf-unsupported">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
      </svg>

      <p class="pdf-unsupported-title">{{ title || 'Documento PDF' }}</p>
      <p class="pdf-unsupported-note">
        Questo browser non può mostrare il documento dentro la pagina.
      </p>

      <a
        :href="pdfUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="pdf-open-link"
      >
        Apri il PDF
      </a>
    </div>
  </div>
</template>



<script setup>
import { ref, computed, defineAsyncComponent } from 'vue'
import { legacyForced } from '@/utils/legacySupport'

// ───────────────────────────────────────────────────────────────────
// PROPS
// ───────────────────────────────────────────────────────────────────

const props = defineProps({
  src: { type: String, required: true },
  title: { type: String, default: '' }
})

// ───────────────────────────────────────────────────────────────────
// URL DEL FILE
// ───────────────────────────────────────────────────────────────────

/**
 * Stessa risoluzione usata da PdfViewer, cosi' il link della ricaduta punta
 * esattamente allo stesso file che il visualizzatore avrebbe aperto.
 * `encodeURI` perche' i nomi dei file contengono spazi.
 */
const pdfUrl = computed(() => {
  const src = props.src
  const url = src.startsWith('http') ? src : (src.startsWith('/') ? src : `/${src}`)
  return encodeURI(url)
})

// ───────────────────────────────────────────────────────────────────
// CONTROLLO DELLE FUNZIONALITA'
// ───────────────────────────────────────────────────────────────────

/**
 * Puo' questo motore ANALIZZARE il worker di pdf.js?
 *
 * PERCHE' SI CONTROLLA LA SINTASSI E NON LE API
 * La prima versione di questo controllo chiedeva se esistessero
 * `Array.prototype.at` e `structuredClone`. Era sbagliato, e il collaudo su un
 * Mac vecchio l'ha dimostrato: core-js polifilla entrambe (`es.array.at` e
 * `web.structured-clone` sono in chunk-vendors), quindi il controllo passava,
 * il visualizzatore si caricava, e pdf.js falliva subito dopo con
 * "Setting up fake worker failed: Invalid character '#'".
 *
 * Un polyfill puo' fingere che una API esista. Non puo' insegnare al motore una
 * grammatica che non conosce.
 *
 * COSA SERVE DAVVERO
 * `pdf.worker.mjs` viene valutato a RUNTIME e non passa mai da Babel, quindi
 * la sua sintassi arriva intatta al motore. Dentro ci sono:
 *   - 61 fra campi e metodi privati (#x)   -> Safari 14.1 / 15
 *   - 1 blocco statico (static { })        -> Safari 16.4   <- il vincolo vero
 *
 * Quando il worker non si puo' creare, pdf.js ripiega sul "fake worker", che
 * valuta lo stesso codice sul thread principale: stessa sintassi, stesso
 * fallimento. Non c'e' scampatoia, e per questo si controlla PRIMA di caricare.
 *
 * Si costruisce una funzione con quella grammatica: se il motore non la sa
 * analizzare lancia un SyntaxError e la risposta e' no. Non si guarda la
 * versione del browser, si guarda cosa il motore sa fare.
 */
function browserCanRenderPdf() {
  try {
    // eslint-disable-next-line no-new-func
    new Function('class P { #f = 1; #m() { return this.#f } static { } }')
    return true
  } catch (e) {
    // SyntaxError (motore troppo vecchio) oppure new Function bloccata da una
    // Content-Security-Policy: in entrambi i casi si ripiega sul link, che e'
    // la direzione sicura.
    return false
  }
}

// Con ?legacy=1 si forza la ricaduta anche su un browser capace, cosi' la si
// puo' guardare senza avere un dispositivo vecchio sottomano.
const supported = browserCanRenderPdf() && !legacyForced()

/** Diventa vero se il chunk non si carica: sintassi non interpretabile o rete giu' */
const chunkFailed = ref(false)

const canShowViewer = computed(() => supported && !chunkFailed.value)

// ───────────────────────────────────────────────────────────────────
// CARICAMENTO PIGRO
// ───────────────────────────────────────────────────────────────────

/**
 * `import()` dinamico: webpack stacca vue-pdf-embed in un chunk a se'.
 * PdfViewer e' il suo unico importatore, quindi se ne va tutta con lui.
 */
const PdfViewerAsync = defineAsyncComponent({
  loader: () => import(
    /* webpackChunkName: "pdf-viewer" */
    '@/components/sections/shared/PdfViewer.vue'
  ),
  delay: 0,
  onError(error, retry, fail) {
    console.error('Caricamento del visualizzatore PDF fallito:', error)
    chunkFailed.value = true
    fail()
  }
})
</script>



<style scoped lang="scss">
@import '@/assets/styles/main.scss';

.pdf-block {
  width: 100%;
}

/* NIENTE gap qui dentro: vedi la nota in cima al file. */
.pdf-unsupported {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: $space-2;
  color: $clr-text-light;

  svg {
    color: $clr-tertiary;
    margin-bottom: $space-1;
  }
}

.pdf-unsupported-title {
  color: $clr-brand-dark;
  font-weight: 600;
  margin: 0 0 $space-0-5 0;
}

.pdf-unsupported-note {
  margin: 0 0 $space-1 0;
  max-width: 34rem;
}

.pdf-open-link {
  color: $clr-brand-dark;
  text-decoration: underline;
  font-weight: 600;
  margin-top: $space-0-5;
}
</style>
